import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, UserCircle, LogIn, ArrowRight } from 'lucide-react';
import DesktopNav from '../components/DesktopNav';
import MobileMenu from '../components/MobileMenu';
import useSeo from '../hooks/useSeo';
import SiteFooter from '../components/SiteFooter';
import { SOCIAL_LINKS } from '../components/SocialIcons';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';

import Hero from '../components/sections/Hero';
import TeamShowcase from '../components/sections/TeamShowcase';
import AboutUs from '../components/sections/AboutUs';
import ServicesGrid from '../components/sections/ServicesGrid';
import WhyChooseUs from '../components/sections/WhyChooseUs';
import HowItWorks from '../components/sections/HowItWorks';
import TestimonialsSection from '../components/sections/TestimonialsSection';
import TreatmentShowcase from '../components/sections/TreatmentShowcase';
import Certifications from '../components/sections/Certifications';
import AwardsRecognition from '../components/sections/AwardsRecognition';
import FinalCTA from '../components/sections/FinalCTA';

const EntoRidHome = ({ onOpenCalculator, onOpenAuth }) => {
  const { isLoggedIn, customer } = useCustomerAuth();
  useSeo(
    'Home',
    'EntoRid provides safe, eco-friendly pest control solutions for homes and businesses. Trusted protection for your family and property.'
  );

  // NOTE: Removed the 5-second auto-open of the calculator/signup modal.
  // It was popping up on top of other modals (video, etc.) and gave the
  // illusion that the video player was "audio-only" because the video modal
  // was being hidden behind the auth modal. Users now reach the calculator
  // via explicit CTAs ("Book Service Now", "Get Free Inspection") which is
  // higher-converting and non-intrusive.

  const openCalculator = (pestKey) => onOpenCalculator && onOpenCalculator(pestKey);

  // Track scroll to collapse the mobile header on scroll-down.
  // Desktop / tablet (sm+) keep the full header at all times.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900" style={{ fontFamily: 'Poppins, Inter, system-ui, sans-serif' }}>
      {/* === Sticky Header === */}
      <header className="sticky top-0 z-50 bg-white shadow-sm">
        {/* Top contact bar (dark navy) — phone · email · location.
            On MOBILE: dissolves on scroll. Desktop/tablet (sm+) always visible. */}
        <div
          className={`bg-brand-blue text-white px-4 text-[12px] sm:text-sm transition-all duration-300 ease-out overflow-hidden
            ${scrolled ? 'max-h-0 opacity-0 py-0 sm:max-h-20 sm:opacity-100 sm:py-2' : 'max-h-20 opacity-100 py-2'}`}
        >
          <div className="max-w-7xl mx-auto flex justify-between items-center gap-3">
            <div className="flex items-center gap-3 sm:gap-6 min-w-0">
              <a href="tel:+917819988010" className="flex items-center gap-2 hover:text-brand-green-light transition-colors whitespace-nowrap">
                <Phone size={14} />
                <span>+91-7819988010</span>
              </a>
              <a href="mailto:info.entorid@gmail.com" className="hidden sm:flex items-center gap-2 hover:text-brand-green-light transition-colors truncate">
                <Mail size={14} />
                <span className="truncate">info.entorid@gmail.com</span>
              </a>
            </div>
            <div className="hidden sm:flex items-center gap-2 whitespace-nowrap">
              <MapPin size={14} />
              <span>Serving Entire Uttarakhand</span>
            </div>

            {/* Social icons — MOBILE ONLY (sit next to phone number on the right). On desktop they live in the brand strip below. */}
            <div className="flex sm:hidden items-center gap-1 shrink-0" data-testid="topbar-social-mobile">
              {SOCIAL_LINKS.map((s) => (
                <a
                  key={s.key}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  title={s.label}
                  className={`w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 text-white inline-flex items-center justify-center transition-all duration-200 active:scale-95 ${s.hover}`}
                  data-testid={`topbar-mobile-${s.key}`}
                >
                  <s.Icon size={12} strokeWidth={2} />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* === BRAND STRIP — different shade, company name + social icons + profile.
            On MOBILE: dissolves on scroll. Desktop/tablet (sm+) always visible. === */}
        <div
          className={`bg-[#0E3A5F] text-white border-t border-white/5 transition-all duration-300 ease-out overflow-hidden
            ${scrolled ? 'max-h-0 opacity-0 sm:max-h-20 sm:opacity-100' : 'max-h-20 opacity-100'}`}
        >
          <div className="max-w-7xl mx-auto px-4 py-1.5 sm:py-2 flex justify-between items-center gap-3">
            {/* Brand name in stylish thin display font */}
            <a
              href="#"
              className="flex items-baseline gap-1.5 group min-w-0"
              data-testid="company-brand-link"
              style={{ fontFamily: '"Cormorant Garamond", "Playfair Display", "Times New Roman", serif' }}
            >
              <span className="text-[15px] sm:text-[19px] md:text-[22px] font-light italic tracking-[0.04em] text-white group-hover:text-brand-green-light transition-colors leading-none truncate">
                The Indian Pest Control
              </span>
              <span className="hidden md:inline text-[9px] uppercase tracking-[0.22em] text-white/55 font-sans font-semibold border-l border-white/20 pl-2 ml-1">
                Featuring EntoRid
              </span>
            </a>
            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
              {/* Social icons — visible on desktop only (on mobile they live in the top contact bar) */}
              <div className="hidden sm:flex items-center gap-1.5 sm:gap-2" data-testid="brand-strip-social">
                {SOCIAL_LINKS.map((s) => (
                  <a
                    key={s.key}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    title={s.label}
                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 text-white inline-flex items-center justify-center transition-all duration-200 hover:scale-110 ${s.hover}`}
                    data-testid={`brand-strip-${s.key}`}
                  >
                    <s.Icon size={13} strokeWidth={2} />
                  </a>
                ))}
              </div>

              {/* Customer Profile icon — visible on every viewport, immediately after social icons (desktop) / right of brand (mobile) */}
              {isLoggedIn ? (
                <Link
                  to="/account"
                  aria-label={`${customer?.name || 'Account'} — Customer Profile`}
                  title={`${customer?.name || 'Account'} — Profile`}
                  className="sm:ml-1 inline-flex items-center gap-1.5 h-7 sm:h-8 pl-1 pr-2 sm:pr-2.5 rounded-full bg-brand-green hover:bg-brand-green-dark text-white border border-brand-green/60 transition-all duration-200 hover:scale-105 shadow-sm"
                  data-testid="brand-strip-profile"
                >
                  <UserCircle size={15} strokeWidth={2.2} />
                  <span className="text-[11px] sm:text-xs font-semibold tracking-wide max-w-[80px] sm:max-w-[120px] truncate">
                    {(customer?.name || 'Account').split(' ')[0]}
                  </span>
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() => onOpenAuth?.('login')}
                  aria-label="Log in to Customer Profile"
                  title="Log in"
                  className="sm:ml-1 inline-flex items-center gap-1.5 h-7 sm:h-8 pl-1 pr-2 sm:pr-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white border border-white/20 transition-all duration-200 hover:scale-105"
                  data-testid="brand-strip-login"
                >
                  <LogIn size={14} strokeWidth={2.2} />
                  <span className="text-[11px] sm:text-xs font-semibold tracking-wide">
                    Log in
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main nav. On MOBILE when scrolled, EntoRid logo centers and the right shows only the hamburger. */}
        <nav className="bg-white border-b relative">
          <div className="max-w-7xl mx-auto px-4 py-3.5 sm:py-4">
            <div className={`flex items-center gap-3 transition-all duration-300 ${
              scrolled ? 'justify-center sm:justify-between' : 'justify-between'
            }`}>
              {/* Logo — when scrolled on mobile, this floats to the absolute center */}
              <a
                href="#"
                className={`flex items-center gap-3 shrink-0 transition-all duration-300 ${
                  scrolled ? 'sm:relative absolute left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0' : ''
                }`}
                data-testid="logo-link"
              >
                <div className="text-2xl font-bold text-brand-green" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  EntoRid
                </div>
                <div className={`text-[11px] text-gray-500 border-l pl-3 leading-tight whitespace-nowrap ${
                  scrolled ? 'hidden xl:block' : 'hidden xl:block'
                }`}>
                  India&apos;s Trusted<br />Pest Experts
                </div>
              </a>

              {/* Desktop nav — hidden on mobile by its own breakpoints */}
              <DesktopNav onOpenAuth={onOpenAuth} hideAccountCta />

              {/* Mobile hamburger — always on the right; when scrolled it sits beside the centered logo */}
              <div className={`${scrolled ? 'ml-auto' : ''} sm:ml-0`}>
                <MobileMenu onCalculatorOpen={openCalculator} />
              </div>
            </div>
          </div>
        </nav>
      </header>

      {/* === Mobile-only hero strip — AI-generated crew photo with subtle
          Ken-Burns zoom + spotlight sweep. Sits directly below the main nav.
          No overlay text — image speaks for itself. === */}
      <div
        className="md:hidden relative w-full h-[200px] overflow-hidden bg-brand-blue"
        data-testid="mobile-hero-strip"
        aria-hidden
      >
        {/* AI hero image with slow Ken-Burns animation */}
        <img
          src="/images/pest-control/hero-strip.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            transformOrigin: 'center center',
            animation: 'heroStripKenBurns 16s ease-in-out infinite alternate',
          }}
        />

        {/* Subtle spotlight sweep — keeps the strip feeling alive */}
        <div
          className="absolute inset-y-0 w-1/3 pointer-events-none mix-blend-screen"
          style={{
            background:
              'linear-gradient(110deg, transparent 0%, rgba(255,255,255,0.10) 45%, rgba(16,185,129,0.18) 50%, rgba(255,255,255,0.10) 55%, transparent 100%)',
            animation: 'heroStripScan 6s ease-in-out infinite',
          }}
        />

        {/* Bottom edge fade so it blends into the hero below */}
        <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-b from-transparent to-brand-blue pointer-events-none" />
      </div>

      {/* === Page Sections === */}
      <main className="pb-24 lg:pb-0">
        <Hero onBookService={openCalculator} onFreeInspection={openCalculator} />
        <Link to="/special-offers" className="block group" data-testid="home-special-offers-cta">
          <div className="relative overflow-hidden bg-gradient-to-r from-rose-700 via-rose-600 to-amber-600 text-white">
            <div className="absolute inset-0 opacity-25" style={{ backgroundImage: 'radial-gradient(circle at 15% 50%, #fbbf24 0, transparent 35%), radial-gradient(circle at 85% 50%, #fff 0, transparent 30%)' }} />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5 flex items-center justify-center gap-3 sm:gap-5 flex-wrap text-center">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/20 px-3 py-1 text-xs font-bold tracking-wide">LIMITED-TIME</span>
              <span className="text-sm sm:text-lg font-bold">Festive & seasonal deals are live — save big on your pest control booking</span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white text-rose-700 px-4 py-1.5 text-sm font-bold group-hover:gap-2.5 transition-all">View Special Offers <ArrowRight size={16} /></span>
            </div>
          </div>
        </Link>
        <TeamShowcase />
        <AboutUs />
        <ServicesGrid onBook={openCalculator} />
        <TreatmentShowcase />
        <Certifications />
        <WhyChooseUs />
        <HowItWorks />
        <TestimonialsSection />
        <AwardsRecognition />
        <FinalCTA onBookService={openCalculator} />
      </main>

      {/* === Footer === */}
      <SiteFooter />
    </div>
  );
};

export default EntoRidHome;
